/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import API from 'js/lib/api';
import redirect from 'js/lib/coursera.redirect';
import { useLocation } from 'js/lib/useRouter';

import { Button, Grid, Typography2, breakpoints } from '@coursera/cds-core';

import { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type componentProps = {
  className?: string;
};

const api = API('/api/thirdPartyOrganizations.v1', { type: 'rest' });

const styles = {
  container: css`
    margin: 0 auto;
    max-width: 396px;
    padding: 0 var(--cds-spacing-100);
    width: 100%;

    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-300) auto;
    }
  `,
};

const ExpiredSSOLink = ({ className }: componentProps) => {
  const location = useLocation();
  const { thirdPartyOrganizationId } = location.query;

  const [loginUrl, setLoginUrl] = React.useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = React.useState<boolean>(!!thirdPartyOrganizationId);

  React.useEffect(() => {
    if (!thirdPartyOrganizationId) return undefined;
    let ignore = false;
    const params = new URLSearchParams();
    params.append('action', 'initiateLoginFlow');
    params.append('id', thirdPartyOrganizationId);
    params.append('returnTo', redirect.getPathname() + redirect.getQueryParams());
    api
      .post(`?${params.toString()}`)
      .then((url: string) => {
        if (ignore) return;
        setLoginUrl(url || undefined);
        setIsLoading(false);
      })
      .catch(() => {
        if (ignore) return;
        setIsLoading(false);
      });
    return () => {
      ignore = true;
    };
  }, [thirdPartyOrganizationId]);

  const proceedToSSOLogin = () => {
    // redirect.setLocation guards against cross-origin URLs; IdP URLs are intentionally external
    if (loginUrl) window.location.assign(loginUrl);
  };

  const renderButton = () => {
    if (isLoading) {
      return (
        <Button variant="primary" size="medium" disabled fullWidth={true}>
          {_t('Loading...')}
        </Button>
      );
    }
    if (loginUrl) {
      return (
        <TrackedCdsButton
          trackingName="proceed_to_sso_login_button"
          variant="primary"
          size="medium"
          onClick={proceedToSSOLogin}
          fullWidth={true}
        >
          {_t('Proceed to SSO Login')}
        </TrackedCdsButton>
      );
    }
    return null;
  };

  return (
    <Grid
      container
      component="section"
      direction="column"
      spacing={24}
      aria-live="polite"
      className={className}
      css={styles.container}
    >
      <Grid item>
        <Typography2 component="h2" variant="titleSmall" align="center">
          {_t('Login link expired')}
        </Typography2>
      </Grid>

      <Grid item>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" align="center">
          {_t('Your login link is no longer valid. Start a new session from your SSO to get a fresh one.')}
        </Typography2>
      </Grid>

      <Grid item>{renderButton()}</Grid>
    </Grid>
  );
};

export default ExpiredSSOLink;
